import React, { useState, useEffect, useCallback } from "react";
import { Spinner, Modal, Form } from "react-bootstrap";
import { FileText, Plus, BookOpen } from "lucide-react";
import { AppButton, EmptyState, ConfirmModal } from "../ui";
import MaterialCard from "./MaterialCard";
import MaterialViewerModal from "./MaterialViewerModal";
import MaterialVideoModal from "./MaterialVideoModal";
import UploadMaterialModal from "./UploadMaterialModal";
import CommonToaster from "../Common/CommonToaster";
import apiClient from "../../utils/apiClient";
import { canDownloadFile } from "../ContentProtection/contentAccess";
import StudentAccessPicker from "../ContentProtection/StudentAccessPicker";
import "./Materials.scss";

const BatchMaterialsPage = ({ batchId, courseId, role, currentUserId }) => {
  const [batchMaterials, setBatchMaterials] = useState([]);
  const [courseMaterials, setCourseMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Upload modal
  const [showUpload, setShowUpload] = useState(false);

  // Edit modal
  const [editMaterial, setEditMaterial] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", description: "", topic: "", category: "other", sessionDate: "" });
  const [saving, setSaving] = useState(false);

  const isAdmin = role === "admin" || role === "superadmin";
  const isInstructor = role === "instructor";
  const canUpload = isAdmin || isInstructor;

  // Viewer modal
  const [viewerMaterial, setViewerMaterial] = useState(null);
  // Video materials stream in a player — students are never offered the file.
  const [playingVideo, setPlayingVideo] = useState(null);
  const [viewerUrl, setViewerUrl] = useState(null);
  const [viewerPdfUrl, setViewerPdfUrl] = useState(null);
  const [showViewer, setShowViewer] = useState(false);

  const fetchMaterials = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/materials/batch/${batchId}`, {
        params: { page: 1, limit: 100 },
      });
      if (data.success) {
        // New API returns { batchMaterials, courseMaterials }
        setBatchMaterials(data.data?.batchMaterials || data.data || []);
        setCourseMaterials(data.data?.courseMaterials || []);
        setTotal(data.pagination?.total || 0);
      }
    } catch {
      setToast({ show: true, message: "Failed to load materials.", type: "error" });
    }
    setLoading(false);
  }, [batchId]);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  // ── Edit handlers ──
  const handleEditOpen = (material) => {
    setEditMaterial(material);
    setEditForm({
      title: material.title || "",
      description: material.description || "",
      topic: material.topic || "",
      category: material.category || "other",
      sessionDate: material.sessionDate ? new Date(material.sessionDate).toISOString().split("T")[0] : "",
      // The file's OWN setting; a file that never chose one has always been downloadable.
      studentAccess: material.studentAccess || "download",
    });
    setShowEdit(true);
  };

  const handleEditSave = async () => {
    if (!editForm.title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
        topic: editForm.topic.trim(),
        category: editForm.category,
      };
      if (editForm.sessionDate) payload.sessionDate = editForm.sessionDate;
      if (!editMaterial.isVideo && editForm.studentAccess) payload.studentAccess = editForm.studentAccess;
      const { data } = await apiClient.put(`/materials/${editMaterial._id}`, payload);
      if (data.success) {
        setToast({ show: true, message: "Material updated.", type: "success" });
        setShowEdit(false);
        setEditMaterial(null);
        fetchMaterials();
      }
    } catch {
      setToast({ show: true, message: "Failed to update material.", type: "error" });
    }
    setSaving(false);
  };

  // ── Delete ──
  const [deleteMaterial, setDeleteMaterial] = useState(null);

  const handleDelete = (material) => setDeleteMaterial(material);

  const confirmDelete = async () => {
    const material = deleteMaterial;
    setDeleteMaterial(null);
    try {
      const { data } = await apiClient.delete(`/materials/${material._id}`);
      if (data.success) {
        setToast({ show: true, message: "Material deleted.", type: "success" });
        fetchMaterials();
      }
    } catch {
      setToast({ show: true, message: "Failed to delete material.", type: "error" });
    }
  };

  // ── View inline ──
  // GET /view returns the URL together with what this user may do with the
  // file; a view-only file comes back with `access.canDownload === false`.
  const handleView = async (material) => {
    setViewerMaterial(material);
    setViewerUrl(null);
    setViewerPdfUrl(null);
    setShowViewer(true);
    try {
      const { data } = await apiClient.get(`/materials/${material._id}/view`);
      if (data.success) {
        setViewerMaterial({ ...material, access: data.data.access });
        setViewerUrl(data.data.url);
        setViewerPdfUrl(data.data.pdfUrl || null);
      } else {
        setToast({ show: true, message: "Failed to load document.", type: "error" });
        setShowViewer(false);
      }
    } catch {
      setToast({ show: true, message: "Failed to load document.", type: "error" });
      setShowViewer(false);
    }
  };

  // ── Download ── (a view-only file opens in the viewer instead)
  const handleDownload = async (material) => {
    if (!canDownloadFile(material)) {
      handleView(material);
      return;
    }
    try {
      const { data } = await apiClient.get(`/materials/${material._id}/download`);
      if (data.success && data.data.downloadable !== false) {
        window.open(data.data.url, "_blank");
      }
    } catch {
      setToast({ show: true, message: "Failed to download material.", type: "error" });
    }
  };

  const handleCloseViewer = () => {
    setShowViewer(false);
    setViewerMaterial(null);
    setViewerUrl(null);
  };

  // ── Upload success ──
  const handleUploadSuccess = () => {
    setToast({ show: true, message: "Material uploaded successfully!", type: "success" });
    fetchMaterials();
  };

  const allEmpty = batchMaterials.length === 0 && courseMaterials.length === 0;

  return (
    <div className="materials-page">
      {/* Header */}
      <div className="materials-page__header">
        <div className="d-flex align-items-center gap-2">
          <h3 className="materials-page__title">
            <FileText size={18} />
            Class Materials
          </h3>
          {(total > 0 || courseMaterials.length > 0) && (
            <span className="materials-page__count">{total + courseMaterials.length}</span>
          )}
        </div>

        {canUpload && (
          <AppButton variant="default" size="sm" onClick={() => setShowUpload(true)}>
            <Plus size={14} />
            Upload Material
          </AppButton>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="materials-spinner">
          <Spinner animation="border" />
        </div>
      ) : allEmpty ? (
        <EmptyState
          icon={<FileText size={40} />}
          title="No materials yet"
          description={canUpload ? "Upload class PPTs, scripts, or notes." : "Class materials will appear here."}
          action={
            canUpload ? (
              <AppButton variant="default" size="sm" onClick={() => setShowUpload(true)}>
                <Plus size={14} />
                Upload Material
              </AppButton>
            ) : null
          }
        />
      ) : (
        <>
          {/* Batch-level Materials */}
          {batchMaterials.length > 0 && (
            <div className="materials-page__section">
              <h6 className="materials-page__section-title d-flex align-items-center gap-1 mb-2" style={{ fontSize: "0.8125rem", color: "#6b7280" }}>
                <FileText size={14} />
                Batch Materials
                <span className="badge bg-secondary bg-opacity-25 text-secondary ms-1" style={{ fontSize: "0.65rem" }}>{batchMaterials.length}</span>
              </h6>
              <div className="materials-page__grid">
                {batchMaterials.map((m) => (
                  <MaterialCard
                    key={m._id}
                    material={m}
                    role={role}
                    currentUserId={currentUserId}
                    onDownload={handleDownload}
                    onView={handleView}
                    onPlay={setPlayingVideo}
                    onEdit={handleEditOpen}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Course-level Materials (inherited, read-only for non-admins) */}
          {courseMaterials.length > 0 && (
            <div className="materials-page__section" style={{ marginTop: batchMaterials.length > 0 ? "1.5rem" : 0 }}>
              <h6 className="materials-page__section-title d-flex align-items-center gap-1 mb-2" style={{ fontSize: "0.8125rem", color: "#6b7280" }}>
                <BookOpen size={14} />
                Course Materials
                <span className="badge bg-primary bg-opacity-25 text-primary ms-1" style={{ fontSize: "0.65rem" }}>{courseMaterials.length}</span>
                <span className="text-muted ms-1" style={{ fontSize: "0.7rem", fontWeight: 400 }}>— shared across all batches</span>
              </h6>
              <div className="materials-page__grid">
                {courseMaterials.map((m) => (
                  <MaterialCard
                    key={m._id}
                    material={m}
                    role={isAdmin ? role : "student"}
                    currentUserId={currentUserId}
                    onDownload={handleDownload}
                    onView={handleView}
                    onPlay={setPlayingVideo}
                    onEdit={isAdmin ? handleEditOpen : undefined}
                    onDelete={isAdmin ? handleDelete : undefined}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <MaterialVideoModal material={playingVideo} onClose={() => setPlayingVideo(null)} />

      {/* Upload Modal */}
      <UploadMaterialModal
        show={showUpload}
        onClose={() => setShowUpload(false)}
        batchId={batchId}
        courseId={courseId}
        onSuccess={handleUploadSuccess}
      />

      {/* Edit Modal */}
      <Modal
        show={showEdit}
        onHide={() => !saving && setShowEdit(false)}
        centered
        className="upload-material-modal"
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title className="fs-6 fw-semibold">Edit Material</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-medium">Title</Form.Label>
            <Form.Control
              type="text"
              value={editForm.title}
              onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
              disabled={saving}
              size="sm"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-medium">Category</Form.Label>
            <Form.Select
              value={editForm.category}
              onChange={(e) => setEditForm((f) => ({ ...f, category: e.target.value }))}
              disabled={saving}
              size="sm"
            >
              <option value="ppt">PPT / Presentation</option>
              <option value="script">Script</option>
              <option value="notes">Notes</option>
              <option value="other">Other</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-medium">Topic</Form.Label>
            <Form.Control
              type="text"
              value={editForm.topic}
              onChange={(e) => setEditForm((f) => ({ ...f, topic: e.target.value }))}
              disabled={saving}
              size="sm"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-medium">Session Date</Form.Label>
            <Form.Control
              type="date"
              value={editForm.sessionDate}
              onChange={(e) => setEditForm((f) => ({ ...f, sessionDate: e.target.value }))}
              disabled={saving}
              size="sm"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="small fw-medium">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={editForm.description}
              onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
              disabled={saving}
              size="sm"
            />
          </Form.Group>
          {editMaterial && !editMaterial.isVideo && (
            <div className="mt-3">
              <StudentAccessPicker
                value={editForm.studentAccess}
                onChange={(studentAccess) => setEditForm((f) => ({ ...f, studentAccess }))}
                fileType={editMaterial.fileType}
                disabled={saving}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <AppButton variant="outline" size="sm" onClick={() => setShowEdit(false)} disabled={saving}>
            Cancel
          </AppButton>
          <AppButton variant="default" size="sm" onClick={handleEditSave} disabled={saving || !editForm.title.trim()}>
            {saving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
          </AppButton>
        </Modal.Footer>
      </Modal>

      {/* Document Viewer Modal */}
      <MaterialViewerModal
        show={showViewer}
        material={viewerMaterial}
        url={viewerUrl}
        pdfUrl={viewerPdfUrl}
        onClose={handleCloseViewer}
        onDownload={handleDownload}
      />

      {/* Toaster */}
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <ConfirmModal
        show={!!deleteMaterial}
        onHide={() => setDeleteMaterial(null)}
        onConfirm={confirmDelete}
        title="Delete Material"
        message={`Delete "${deleteMaterial?.title}"?`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

export default BatchMaterialsPage;
